import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import Pagination from '../components/ui/Pagination';
import { apiService } from '../services/api';
import { formatDate } from '../lib/utils';
import { useToastNotifications } from '../components/ui/Toast';
import { 
  Smartphone, 
  Edit, 
  RefreshCw,
  Search,
  User,
  Calendar,
  AlertCircle,
  Trash2,
  Trash2 as BulkDeleteIcon,
  Filter,
  X,
  Tv
} from 'lucide-react';
import ConfirmModal from '../components/ui/ConfirmModal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/Select';

interface Trial {
  id: string;
  username: string;
  deviceId: string | null;
  userAgent: string;
  packageId: number;
  duration: number;
  generatedAt: string;
  expiresAt: string | null;
  blockedUntil: string;
  isBlocked: boolean;
  isExpired: boolean;
  isActive?: boolean; // Novo campo para indicar se está ativo
  useProxy?: boolean | null; // null = automático (segue CMS)
  useProxyConfigured?: boolean;
  smartTv?: SmartTvLicense | null;
}

interface SmartTvLicense {
  deviceId: string;
  platform: 'webos' | 'tizen' | 'roku' | 'web';
  status: 'trial' | 'active' | 'expired' | 'free';
  trialEndsAt: string | null;
  activeUntil: string | null;
  activations: number;
}

const SMART_TV_PREFIXES: Record<string, string> = {
  LGTV_: 'Código Smart TV (LG webOS)',
  TIZEN_: 'Código Smart TV (Samsung Tizen)',
  ROKU_: 'Código Smart TV (Roku)',
  WEB_: 'Código Smart TV (navegador de teste)'
};

const smartTvPrefix = (deviceId: string | null) =>
  Object.keys(SMART_TV_PREFIXES).find(prefix => deviceId?.toUpperCase().startsWith(prefix)) || null;

const deviceTypeLabel = (deviceId: string | null) => {
  const prefix = smartTvPrefix(deviceId);
  if (prefix) return SMART_TV_PREFIXES[prefix];
  return deviceId?.startsWith('ANDROID_') ? 'Android ID' : 'MAC Address';
};

const DeviceManagement: React.FC = () => {
  const [trials, setTrials] = useState<Trial[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTrials, setTotalTrials] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingDevice, setEditingDevice] = useState<Trial | null>(null);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingDeviceId, setDeletingDeviceId] = useState<string | null>(null);
  
  // Estados para remoção em massa
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [filterType, setFilterType] = useState<'inactive' | 'expired' | 'both' | 'all'>('inactive');
  const [previewData, setPreviewData] = useState<any>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activatingTrial, setActivatingTrial] = useState<Trial | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [platformFilter, setPlatformFilter] = useState<'all' | 'android' | 'smarttv'>('all');
  const [smartTvCount, setSmartTvCount] = useState(0);
  const [androidCount, setAndroidCount] = useState(0);

  const { showSuccess, showError } = useToastNotifications();

  const isSmartTvDevice = (deviceId: string | null) => !!smartTvPrefix(deviceId);

  const loadTrials = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getTrials(currentPage, 20, searchTerm, platformFilter);
      
      if (response.success) {
        let trialsWithDevice = response.data.filter((trial: Trial) => trial.deviceId);
        // Fallback se a API ainda não filtrar por plataforma
        if (typeof response.pagination?.smartTvTotal !== 'number' && platformFilter !== 'all') {
          trialsWithDevice = trialsWithDevice.filter((trial: Trial) => {
            const smart = isSmartTvDevice(trial.deviceId);
            return platformFilter === 'smarttv' ? smart : !smart;
          });
        }
        setTrials(trialsWithDevice);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalTrials(response.pagination?.total || trialsWithDevice.length);
        if (typeof response.pagination?.smartTvTotal === 'number') {
          setSmartTvCount(response.pagination.smartTvTotal);
          setAndroidCount(response.pagination.androidTotal || 0);
        } else {
          const smart = trialsWithDevice.filter((trial: Trial) => isSmartTvDevice(trial.deviceId)).length;
          setSmartTvCount(platformFilter === 'smarttv' ? trialsWithDevice.length : smart);
          setAndroidCount(platformFilter === 'android' ? trialsWithDevice.length : trialsWithDevice.length - smart);
        }
      }
    } catch (error: any) {
      console.error('Erro ao carregar dispositivos:', error);
      showError(error.response?.data?.error || 'Erro ao carregar dispositivos');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, platformFilter, showError]);

  useEffect(() => {
    loadTrials();
  }, [loadTrials]);

  // Resetar página para 1 quando a busca ou o filtro de plataforma mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, platformFilter]);

  const handleEdit = (trial: Trial) => {
    setEditingDevice(trial);
    setNewUsername(trial.username);
    setNewPassword('');
  };

  const handleCancelEdit = () => {
    setEditingDevice(null);
    setNewUsername('');
    setNewPassword('');
  };

  const handleUpdate = async () => {
    if (!editingDevice || !editingDevice.deviceId) {
      showError('Device ID não encontrado');
      return;
    }

    // Validar: precisa ter pelo menos username, senha OU alteração de proxy para atualizar
    const hasUsernameChange = newUsername && newUsername.trim() !== '' && newUsername !== editingDevice.username;
    const hasPassword = newPassword && newPassword.trim() !== '';
    
    if (!hasUsernameChange && !hasPassword) {
      showError('É necessário fornecer novo username e/ou nova senha');
      return;
    }

    try {
      setIsUpdating(true);

      const response = await apiService.updateDeviceUser(
        editingDevice.deviceId,
        hasUsernameChange ? newUsername : undefined,
        hasPassword ? newPassword : undefined // Enviar apenas se foi preenchido
      );

      if (response.success) {
        showSuccess('Usuário atualizado com sucesso! O app será atualizado automaticamente.');
        handleCancelEdit();
        loadTrials(); // Recarregar lista
      } else {
        showError(response.error || 'Erro ao atualizar usuário');
      }
    } catch (error: any) {
      console.error('Erro ao atualizar usuário:', error);
      showError(error.response?.data?.error || 'Erro ao atualizar usuário do dispositivo');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (deviceId: string) => {
    if (!deviceId) {
      showError('Device ID não encontrado');
      return;
    }

    // Confirmar antes de deletar
    if (!window.confirm('Tem certeza que deseja deletar este dispositivo? Após deletar, será possível gerar um novo teste para este MAC/Android ID.')) {
      return;
    }

    try {
      setDeletingDeviceId(deviceId);
      const response = await apiService.deleteDevice(deviceId);

      if (response.success) {
        showSuccess(response.message || 'Dispositivo deletado com sucesso! Agora é possível gerar um novo teste.');
        loadTrials(); // Recarregar lista
      } else {
        showError(response.error || 'Erro ao deletar dispositivo');
      }
    } catch (error: any) {
      console.error('Erro ao deletar dispositivo:', error);
      showError(error.response?.data?.error || 'Erro ao deletar dispositivo');
    } finally {
      setDeletingDeviceId(null);
    }
  };

  const handleActivateSmartTv = async () => {
    if (!activatingTrial?.deviceId) return;
    try {
      setIsActivating(true);
      const response = await apiService.activateSmartTv(activatingTrial.deviceId);
      if (response.success) {
        const until = response.data?.activeUntil ? formatDate(response.data.activeUntil) : '';
        showSuccess(`Smart TV ativada${until ? ` até ${until}` : ''}. 1 crédito utilizado.`);
        setActivatingTrial(null);
        loadTrials();
      } else {
        showError(response.error || 'Erro ao ativar Smart TV');
      }
    } catch (error: any) {
      console.error('Erro ao ativar Smart TV:', error);
      showError(error.response?.data?.error || 'Erro ao ativar Smart TV');
    } finally {
      setIsActivating(false);
    }
  };

  const renderSmartTvStatus = (license: SmartTvLicense) => {
    if (license.status === 'free') {
      return <span className="text-sm text-blue-600"><strong>Smart TV:</strong> Versão gratuita</span>;
    }
    if (license.status === 'active') {
      return <span className="text-sm text-green-600"><strong>Smart TV:</strong> Ativada até {formatDate(license.activeUntil)}</span>;
    }
    if (license.status === 'trial') {
      return <span className="text-sm text-yellow-600"><strong>Smart TV:</strong> Teste grátis até {formatDate(license.trialEndsAt)}</span>;
    }
    return <span className="text-sm text-red-600"><strong>Smart TV:</strong> Aguardando ativação</span>;
  };

  // A busca agora é feita no servidor, não precisamos mais filtrar localmente
  // Removido: filteredTrials - a API já retorna os resultados filtrados

  const formatDeviceId = (deviceId: string | null) => {
    if (!deviceId) return 'N/A';

    const tvPrefix = smartTvPrefix(deviceId);
    if (tvPrefix) {
      const code = deviceId.substring(tvPrefix.length).toUpperCase();
      return /^[0-9A-F]{12}$/.test(code) ? code.match(/.{2}/g)!.join(':') : code;
    }
    
    // Se começar com ANDROID_, remover prefixo e formatar
    if (deviceId.startsWith('ANDROID_')) {
      const androidId = deviceId.substring(8); // Remove "ANDROID_"
      // Android ID tem 16 caracteres hexadecimais - formatar com dois pontos a cada 2 caracteres
      if (androidId.length === 16 && /^[0-9A-F]+$/i.test(androidId)) {
        return androidId.match(/.{1,2}/g)?.join(':').toUpperCase() || androidId;
      }
      return androidId.toUpperCase();
    }
    
    // Se for MAC address (12 caracteres hex sem dois pontos), formatar
    if (deviceId.length === 12 && /^[0-9A-F]+$/i.test(deviceId)) {
      return deviceId.match(/.{1,2}/g)?.join(':').toUpperCase() || deviceId;
    }
    
    // Se for Android ID sem prefixo (16 caracteres), formatar também
    if (deviceId.length === 16 && /^[0-9A-F]+$/i.test(deviceId)) {
      return deviceId.match(/.{1,2}/g)?.join(':').toUpperCase() || deviceId;
    }
    
    return deviceId;
  };

  // Função para fazer preview da remoção em massa
  const handlePreviewBulkDelete = async () => {
    try {
      setIsLoadingPreview(true);
      const response = await apiService.getBulkDeletePreview(filterType);
      
      if (response.success) {
        setPreviewData(response.data);
      } else {
        showError(response.error || 'Erro ao fazer preview');
      }
    } catch (error: any) {
      console.error('Erro ao fazer preview:', error);
      showError(error.response?.data?.error || 'Erro ao fazer preview de remoção');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Função para executar remoção em massa
  const handleExecuteBulkDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await apiService.bulkDeleteDevices(filterType);
      
      if (response.success) {
        showSuccess(response.message || `${response.data.deletedCount} dispositivo(s) removido(s) com sucesso`);
        setShowBulkDeleteModal(false);
        setPreviewData(null);
        loadTrials(); // Recarregar lista
      } else {
        showError(response.error || 'Erro ao remover dispositivos');
      }
    } catch (error: any) {
      console.error('Erro ao remover dispositivos em massa:', error);
      showError(error.response?.data?.error || 'Erro ao remover dispositivos em massa');
    } finally {
      setIsDeleting(false);
      setShowConfirmModal(false);
    }
  };

  // Resetar preview quando mudar filtros
  useEffect(() => {
    if (showBulkDeleteModal) {
      setPreviewData(null);
    }
  }, [filterType, showBulkDeleteModal]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Gerenciamento de Dispositivos</h1>
          <p className="text-muted-foreground mt-1">
            Android (teste de linha) e Smart TV AobaPlay (LG, Samsung e Roku). Na Smart TV, use o código do aparelho para ativar 1 ano com 1 crédito.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowBulkDeleteModal(true)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
        >
          <BulkDeleteIcon className="h-4 w-4 mr-2" />
          Remover em Massa
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Dispositivos</CardTitle>
            <Smartphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalTrials}</div>
            <p className="text-xs text-muted-foreground">Dispositivos com testes gerados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Dispositivos Bloqueados</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {trials.filter(t => t.isBlocked).length}
            </div>
            <p className="text-xs text-muted-foreground">Aguardando cooldown</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Testes Expirados</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {trials.filter(t => t.isExpired).length}
            </div>
            <p className="text-xs text-muted-foreground">Testes que expiraram</p>
          </CardContent>
        </Card>
      </div>

      {/* Busca */}
      <Card>
        <CardHeader>
          <CardTitle>Buscar Dispositivo</CardTitle>
          <CardDescription>
            Busque por username, Android ID ou código da Smart TV (MAC exibido no app / login)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Username, Android ID ou código Smart TV (ex: A1:B2:C3:D4:E5:F6)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant={platformFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setPlatformFilter('all')}
            >
              Todos ({smartTvCount + androidCount || totalTrials})
            </Button>
            <Button
              type="button"
              size="sm"
              variant={platformFilter === 'android' ? 'default' : 'outline'}
              onClick={() => setPlatformFilter('android')}
            >
              <Smartphone className="h-4 w-4 mr-2" />
              Android ({androidCount})
            </Button>
            <Button
              type="button"
              size="sm"
              variant={platformFilter === 'smarttv' ? 'default' : 'outline'}
              onClick={() => setPlatformFilter('smarttv')}
            >
              <Tv className="h-4 w-4 mr-2" />
              Smart TV ({smartTvCount})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Dispositivos */}
      <Card>
        <CardHeader>
          <CardTitle>Dispositivos Registrados</CardTitle>
          <CardDescription>
            Android: usuário e validade da linha. Smart TV: status do app AobaPlay e botão para ativar/renovar 1 ano.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : trials.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {platformFilter === 'smarttv' ? (
                <Tv className="h-12 w-12 mx-auto mb-4 opacity-50" />
              ) : (
                <Smartphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
              )}
              <p>
                {searchTerm
                  ? 'Nenhum dispositivo encontrado para a busca'
                  : platformFilter === 'smarttv'
                    ? 'Nenhuma Smart TV registrada ainda. Abra o AobaPlay na TV para o código aparecer aqui.'
                    : 'Nenhum dispositivo encontrado'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {trials.map((trial) => (
                <div
                  key={trial.id}
                  className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                >
                  {editingDevice?.id === trial.id ? (
                    // Modo de edição
                    <div className="space-y-4">
                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                          <Label htmlFor="newUsername">Novo Username</Label>
                          <Input
                            id="newUsername"
                            value={newUsername}
                            onChange={(e) => setNewUsername(e.target.value)}
                            placeholder={trial.username}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="newPassword">Nova Senha (opcional)</Label>
                          <Input
                            id="newPassword"
                            type="text" // Texto visível para facilitar edição
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Digite a senha para login automático no app"
                          />
                          <p className="text-sm text-muted-foreground">
                            A senha será usada para login automático no aplicativo quando o MAC for verificado.
                          </p>
                        </div>
                        {/* Proxy é controlado diretamente pelo checkbox na lista (sem precisar editar) */}
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={handleCancelEdit}
                          disabled={isUpdating}
                        >
                          Cancelar
                        </Button>
                        <Button
                          onClick={handleUpdate}
                          disabled={isUpdating}
                        >
                          {isUpdating ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Atualizando...
                            </>
                          ) : (
                            <>
                              <User className="h-4 w-4 mr-2" />
                              Atualizar Usuário
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Modo de visualização
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          {smartTvPrefix(trial.deviceId) ? (
                            <Tv className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <Smartphone className="h-5 w-5 text-muted-foreground" />
                          )}
                          <div>
                            <p className="font-semibold">
                              {formatDeviceId(trial.deviceId)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {deviceTypeLabel(trial.deviceId)}
                            </p>
                          </div>
                        </div>
                        <div className="grid gap-2 md:grid-cols-2 mt-4">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              <strong>Usuário:</strong> {trial.username}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              <strong>Gerado em:</strong> {formatDate(trial.generatedAt)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              <strong>Expira em:</strong> {formatDate(trial.expiresAt)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {trial.isBlocked ? (
                              <>
                                <AlertCircle className="h-4 w-4 text-yellow-500" />
                                <span className="text-sm text-yellow-600">
                                  <strong>Status:</strong> Bloqueado até {formatDate(trial.blockedUntil)}
                                </span>
                              </>
                            ) : trial.isExpired ? (
                              <>
                                <AlertCircle className="h-4 w-4 text-red-500" />
                                <span className="text-sm text-red-600">
                                  <strong>Status:</strong> Expirado
                                </span>
                              </>
                            ) : trial.isActive !== undefined ? (
                              // Usar isActive do backend (mais preciso)
                              trial.isActive ? (
                                <>
                                  <AlertCircle className="h-4 w-4 text-green-500" />
                                  <span className="text-sm text-green-600">
                                    <strong>Status:</strong> Ativo
                                  </span>
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="h-4 w-4 text-gray-500" />
                                  <span className="text-sm text-gray-600">
                                    <strong>Status:</strong> Inativo
                                  </span>
                                </>
                              )
                            ) : (
                              // Fallback: verificar se tem expiresAt válido e não expirado
                              trial.expiresAt && new Date(trial.expiresAt) > new Date() ? (
                                <>
                                  <AlertCircle className="h-4 w-4 text-green-500" />
                                  <span className="text-sm text-green-600">
                                    <strong>Status:</strong> Ativo
                                  </span>
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="h-4 w-4 text-gray-500" />
                                  <span className="text-sm text-gray-600">
                                    <strong>Status:</strong> Inativo
                                  </span>
                                </>
                              )
                            )}
                          </div>
                          {isSmartTvDevice(trial.deviceId) && (
                            <div className="flex items-center gap-2">
                              <Tv className="h-4 w-4 text-muted-foreground" />
                              {trial.smartTv
                                ? renderSmartTvStatus(trial.smartTv)
                                : <span className="text-sm text-red-600"><strong>Smart TV:</strong> Aguardando ativação</span>}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 items-center">
                        {isSmartTvDevice(trial.deviceId) && trial.smartTv?.status !== 'free' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setActivatingTrial(trial)}
                            disabled={isActivating && activatingTrial?.id === trial.id}
                            className="text-green-700 hover:text-green-800 hover:bg-green-50 border-green-300"
                          >
                            <Tv className="h-4 w-4 mr-2" />
                            {trial.smartTv?.status === 'active' ? 'Renovar 1 ano (1 crédito)' : 'Ativar 1 ano (1 crédito)'}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(trial)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(trial.deviceId!)}
                          disabled={deletingDeviceId === trial.deviceId}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          {deletingDeviceId === trial.deviceId ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Deletando...
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Deletar
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Remoção em Massa */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-50 rounded-full">
                    <BulkDeleteIcon className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Remoção em Massa de Dispositivos</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Remova múltiplos dispositivos de uma vez usando filtros
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowBulkDeleteModal(false);
                    setPreviewData(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Filtros */}
              <div className="space-y-4 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="filterType">Tipo de Filtro</Label>
                  <Select value={filterType} onValueChange={(value) => setFilterType(value as any)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo de filtro" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inactive">Apenas Inativos</SelectItem>
                      <SelectItem value="expired">Apenas Expirados</SelectItem>
                      <SelectItem value="both">Inativos ou Expirados</SelectItem>
                      <SelectItem value="all">Todos os Dispositivos</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {filterType === 'inactive' && 'Remove dispositivos inativos (sem plano ativo: MAC órfão, sem linha vinculada, etc.)'}
                    {filterType === 'expired' && 'Remove dispositivos com plano/teste expirado (vencimento no passado)'}
                    {filterType === 'both' && 'Remove todos que não estão ativos (inativos + expirados). Planos anuais ativos são preservados.'}
                    {filterType === 'all' && 'Remove TODOS os dispositivos, inclusive ativos (use com cuidado!)'}
                  </p>
                </div>
              </div>

              {/* Preview */}
              {previewData ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-blue-900 mb-3">Preview da Remoção</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-blue-700">Total de Dispositivos:</p>
                      <p className="text-2xl font-bold text-blue-900">{previewData.totalDevices}</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-700">Serão Removidos:</p>
                      <p className="text-2xl font-bold text-red-600">{previewData.willDelete}</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-700">Ativos (preservados):</p>
                      <p className="text-xl font-semibold text-green-700">{previewData.activeCount ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-700">Expirados:</p>
                      <p className="text-xl font-semibold text-blue-900">{previewData.expiredCount ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-700">Inativos:</p>
                      <p className="text-xl font-semibold text-blue-900">{previewData.inactiveCount ?? 0}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600 text-center">
                    Clique em "Fazer Preview" para ver quantos dispositivos serão removidos
                  </p>
                </div>
              )}

              {/* Ações */}
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBulkDeleteModal(false);
                    setPreviewData(null);
                  }}
                  disabled={isDeleting}
                >
                  Cancelar
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePreviewBulkDelete}
                  disabled={isLoadingPreview || isDeleting}
                >
                  {isLoadingPreview ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Carregando...
                    </>
                  ) : (
                    <>
                      <Filter className="h-4 w-4 mr-2" />
                      Fazer Preview
                    </>
                  )}
                </Button>
                {previewData && previewData.willDelete > 0 && (
                  <Button
                    onClick={() => setShowConfirmModal(true)}
                    disabled={isDeleting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {isDeleting ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Removendo...
                      </>
                    ) : (
                      <>
                        <BulkDeleteIcon className="h-4 w-4 mr-2" />
                        Remover {previewData.willDelete} Dispositivo(s)
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleExecuteBulkDelete}
        title="Confirmar Remoção em Massa"
        message={`Tem certeza que deseja remover ${previewData?.willDelete || 0} dispositivo(s)?\n\nEsta ação não pode ser desfeita.`}
        type="danger"
        confirmText="Sim, Remover"
        cancelText="Cancelar"
        isLoading={isDeleting}
      />

      <ConfirmModal
        isOpen={!!activatingTrial}
        onClose={() => !isActivating && setActivatingTrial(null)}
        onConfirm={handleActivateSmartTv}
        title="Ativar AobaPlay Smart TV"
        message={`Ativar o aparelho ${formatDeviceId(activatingTrial?.deviceId ?? null)} por 1 ano?\n\nSerá descontado 1 crédito do seu saldo.${
          activatingTrial?.smartTv?.status === 'active' ? '\nO novo período será somado à validade atual.' : ''
        }`}
        type="info"
        confirmText="Sim, ativar"
        cancelText="Cancelar"
        isLoading={isActivating}
      />
    </div>
  );
};

export default DeviceManagement;

